import { useEffect, useMemo, useSyncExternalStore, type ReactNode } from "react";
import { useThemeModePreference } from "@/app/theme";
import { datasetAnalyticsStore } from "@/components/analytics";
import type { DatasetFilterController } from "./DatasetFilterController";
import { FilterSearchCombobox, type FilterSearchCandidate } from "./FilterSearchCombobox";
import { appliedCandidateValues, buildLabelRecommendations } from "./labelRecommendations";

// 입력은 이름 검색도 겸한다. 그래서 후보를 받지 못해도 검색은 쓸 수 있다고 함께 알린다.
const CATALOG_ERROR_MESSAGE =
  "라벨 조건 목록을 불러오지 못했습니다. 이름 검색은 그대로 쓸 수 있습니다.";

export type DatasetSearchInputProps = {
  controller: DatasetFilterController;
  placeholder?: string;
  ariaLabel?: string;
  className?: string;
};

/**
 * 데이터셋 이름 검색 + 라벨 조건 후보 콤보박스다. `DatasetFilterController` 에 연결된 표시 계층이다.
 *
 * 입력은 표시 이름 검색이다(컨트롤러가 debounce 뒤에 커밋한다). 포커스하면 바로 추천 목록을
 * 표시한다. 추천 목록은 facet 축 값과 generic 라벨을 데이터셋 수 내림차순으로 합친 목록이다
 * (labelRecommendations.ts). 타이핑하면 같은 순서로 좁힌다. 후보를 고르면 facet 선택 또는 라벨
 * 조건에 추가되어 칩이 표시되고, 입력은 비운다. 그래서 후보를 찾던 글자가 이름 검색으로 남지
 * 않는다. 이미 적용한 조건은 후보에서 뺀다. Datasets·Review 가 같은 컴포넌트를 쓴다.
 *
 * 건수는 전역 `datasetAnalyticsStore` 에서 읽는다. 대시보드와 같은 응답이라 마운트할 때 한 번만
 * 가져온다(`ensure`). 아직 없으면 카탈로그 순서로 먼저 표시하고, 도착하면 다시 정렬한다.
 */
export function DatasetSearchInput({
  controller,
  placeholder = "이름으로 검색하거나 라벨 조건을 골라 주세요.",
  ariaLabel = "Dataset 검색",
  className,
}: DatasetSearchInputProps): ReactNode {
  const snapshot = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
    controller.getSnapshot,
  );
  const analytics = useSyncExternalStore(
    datasetAnalyticsStore.subscribe,
    datasetAnalyticsStore.getSnapshot,
    datasetAnalyticsStore.getSnapshot,
  );
  useEffect(() => {
    datasetAnalyticsStore.ensure("week");
  }, []);
  // 라벨이 아닌 축의 칩 색은 테마를 따른다(다크=밝은 단계, 라이트=어두운 단계).
  const [themeMode] = useThemeModePreference();

  const analyticsData = analytics.week.data ?? analytics.day.data;
  const candidates = useMemo(
    () =>
      buildLabelRecommendations({
        groups: snapshot.filter.groups,
        labelOptions: snapshot.labelTokenOptions,
        labelColors: snapshot.labelColors,
        analytics: analyticsData,
        themeMode,
      }),
    [
      snapshot.filter.groups,
      snapshot.labelTokenOptions,
      snapshot.labelColors,
      analyticsData,
      themeMode,
    ],
  );
  const applied = useMemo(
    () =>
      appliedCandidateValues(
        snapshot.filter.selection,
        snapshot.labelTokens.map((token) => token.value),
      ),
    [snapshot.filter.selection, snapshot.labelTokens],
  );
  return (
    <FilterSearchCombobox
      className={className}
      value={snapshot.search.inputValue}
      placeholder={placeholder}
      ariaLabel={ariaLabel}
      candidates={candidates}
      appliedValues={applied}
      loading={snapshot.loading && candidates.length === 0}
      errorMessage={
        snapshot.errorMessage !== null && candidates.length === 0
          ? CATALOG_ERROR_MESSAGE
          : undefined
      }
      onValueChange={(value) => controller.setSearchInput(value)}
      onSearch={() => controller.submitSearch()}
      onSelectCandidate={(candidate: FilterSearchCandidate) =>
        controller.selectLabelCandidate({
          value: candidate.value,
          label: candidate.label,
          ...(candidate.accentColor ? { accentColor: candidate.accentColor } : {}),
        })
      }
    />
  );
}
